import { useState, useEffect, useMemo } from "react";
import {
  useFetchTopicsQuery,
  useFetchSubtopicsQuery,
  useUpdateUserTopicsMutation,
} from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";

const searchInputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3 pl-10",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

interface OnboardingInterestsStepProps {
  userId: string;
  userTopics?: UserProfile["user_topics"];
  onContinue: () => void;
}

export function OnboardingInterestsStep({
  userId,
  userTopics,
  onContinue,
}: OnboardingInterestsStepProps) {
  const { data: topics = [], isLoading: isLoadingTopics } =
    useFetchTopicsQuery();
  const { data: subtopics = [], isLoading: isLoadingSubtopics } =
    useFetchSubtopicsQuery();
  const [updateUserTopics, { isLoading: isSaving }] =
    useUpdateUserTopicsMutation();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize from existing user topics
  useEffect(() => {
    if (userTopics) {
      const ids = new Set<string>();
      userTopics.forEach((ut) => {
        if (ut.topic_id) ids.add(ut.topic_id);
        if (ut.subtopic_id) ids.add(ut.subtopic_id);
      });
      setSelectedIds(ids);
    }
  }, [userTopics]);

  const selectableItems = useMemo(() => {
    const items: Array<{
      id: string;
      name: string;
      emoji: string | null;
      type: "topic" | "subtopic";
    }> = [];

    topics.forEach((topic) => {
      items.push({
        id: topic.id,
        name: topic.name,
        emoji: topic.emoji || null,
        type: "topic",
      });
    });

    subtopics.forEach((subtopic) => {
      items.push({
        id: subtopic.id,
        name: subtopic.name,
        emoji: subtopic.parent_topic?.emoji || null,
        type: "subtopic",
      });
    });

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return items.filter((item) => item.name.toLowerCase().includes(query));
    }

    return items;
  }, [topics, subtopics, searchQuery]);

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    if (selectedIds.size > 0) {
      try {
        await updateUserTopics({
          userId,
          selectedIds: Array.from(selectedIds),
        }).unwrap();
      } catch (error) {
        console.error("[OnboardingInterests] Save failed:", error);
      }
    }
    onContinue();
  };

  const isLoading = isLoadingTopics || isLoadingSubtopics;

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto autohide-scrollbar px-8 pt-4 pb-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h1 className="text-h1 text-text-primary">
              What brings you joy?
            </h1>
            <p className="text-body-1 text-text-primary">
              Choose some interests so we can connect you with the clubs and
              people who share them.
            </p>
          </div>

          {/* Search input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <Icon name="search" size={16} className="text-text-secondary" />
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search interests..."
              className={searchInputClassName}
            />
          </div>

          {/* Interests grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 rounded-full border-2 border-text-tertiary border-t-transparent animate-spin" />
            </div>
          ) : selectableItems.length === 0 ? (
            <p className="text-body-2 text-text-tertiary text-center py-8">
              {searchQuery ? "No interests found" : "No interests available"}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectableItems.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleSelection(item.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-full border transition-colors",
                      isSelected
                        ? "border-text-primary bg-text-primary/10"
                        : "border-border-tertiary hover:border-border-secondary"
                    )}
                  >
                    {item.emoji && (
                      <span className="text-base">{item.emoji}</span>
                    )}
                    <span
                      className={cn(
                        "text-body-2",
                        isSelected ? "text-text-primary" : "text-text-secondary"
                      )}
                    >
                      {item.name}
                    </span>
                    {isSelected && (
                      <Icon
                        name="check"
                        size={14}
                        className="text-text-primary"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {selectedIds.size > 0 && (
            <p className="text-body-3 text-text-tertiary">
              {selectedIds.size} selected
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-8 pb-6 pt-4">
        <button
          onClick={handleContinue}
          disabled={isSaving}
          className="w-full py-3.5 rounded-full bg-text-primary text-text-reversed text-body-1-medium font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Continue"}
        </button>
      </div>
    </div>
  );
}
